import { apiClient } from '../../apiHelper';
import { useEdit, useSingleFetch } from '../react-query';
import rolesQueryKeys from './roles-query-keys';

export type RoleCategory = 'CLINICAL' | 'ADMINISTRATIVE' | 'EXTERNAL';

export interface Role {
  id: string;
  key: string;
  name: string;
  category: RoleCategory;
  is_system: boolean;
  assignable_levels: string[];
}

export interface ActionDef {
  key: string;
  entity: string;
  description?: string | null;
}

export interface MatrixCell {
  action: string;
  entity: string;
  allowed: boolean;
}

export interface RoleMatrix {
  role_id: string;
  role_key: string;
  cells: MatrixCell[];
}

export function useRoles(config = {}) {
  const fn = async (): Promise<Role[]> => {
    const res = await apiClient.get('roles');
    return (res?.data ?? res) as Role[];
  };
  return useSingleFetch(fn, rolesQueryKeys.list(), 'roles', config);
}

export function useActionCatalog(config = {}) {
  const fn = async (): Promise<ActionDef[]> => {
    const res = await apiClient.get('action-catalog');
    return (res?.data ?? res) as ActionDef[];
  };
  return useSingleFetch(fn, rolesQueryKeys.actions(), 'actions', config);
}

export function useRoleMatrix(roleId: string | undefined, config = {}) {
  const fn = async (): Promise<RoleMatrix | null> => {
    if (!roleId) return null;
    const res = await apiClient.get(`roles/${roleId}/permissions`);
    return (res?.data ?? res) as RoleMatrix;
  };
  return useSingleFetch(
    fn,
    roleId ? rolesQueryKeys.matrix(roleId) : null,
    roleId ?? '',
    config
  );
}

export function useSetRoleMatrix(
  roleId: string,
  onSuccess?: (response: any, data: { cells: MatrixCell[] }) => void,
  onError?: (error: any, data: { cells: MatrixCell[] }) => void
) {
  const fn = async (data: { cells: MatrixCell[] }): Promise<RoleMatrix> => {
    const res = await apiClient.put(`roles/${roleId}/permissions`, data);
    return (res?.data ?? res) as RoleMatrix;
  };
  return useEdit(
    fn,
    rolesQueryKeys.all,
    'Error while saving permissions',
    'Permissions saved',
    onSuccess,
    onError
  );
}
